/**
 * @file TransferCreateDialog.tsx
 * @description Создание черновика перемещения: склад-источник + комментарий
 * (phase19.23 этап B).
 *
 * Диалог, а не отдельная страница `/new`: у документа всего два поля шапки, а
 * состав строк всё равно набирается в карточке. Склад-источник один на
 * документ — так устроена бумага, под ней подписывается один МОЛ «отпустил».
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { SelectField } from "../../ui/SelectField";
import { Textarea } from "../../ui/textarea";
import { useNotify } from "../../ui/NotifyProvider";
import { WmsFieldLabel } from "../WmsFieldLabel";
import { transfersApi, type TransferDto } from "../../../lib/api-transfers";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Черновик создан — страница уходит в его карточку. */
  onCreated: (transfer: TransferDto) => void;
}

export function TransferCreateDialog({ open, onClose, onCreated }: Props) {
  const notify = useNotify();
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    warehousesApi
      .list()
      .then((data) => {
        if (cancelled) return;
        setWarehouses(data);
        const def = data.find((w) => w.isDefault) ?? data[0];
        if (def) setWarehouseId((prev) => prev || def.id);
      })
      .catch(() => {
        if (!cancelled) notify.error("Не удалось загрузить склады");
      });
    return () => {
      cancelled = true;
    };
  }, [open, notify]);

  async function submit() {
    if (!warehouseId) return;
    setCreating(true);
    try {
      const created = await transfersApi.create({
        fromWarehouseId: warehouseId,
        notes: notes.trim() || undefined,
      });
      notify.success(`Черновик ${created.transferNumber} создан`);
      setNotes("");
      onCreated(created);
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось создать перемещение"));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новое перемещение</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <p className="text-sm text-on-surface-variant">
            Склад-источник задаётся один на документ: строки описывают, куда
            уезжает товар — в ячейку этого же склада или на другой склад.
          </p>
          <WmsFieldLabel label="Склад-источник">
            <SelectField
              value={warehouseId}
              onChange={setWarehouseId}
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              placeholder="— склад —"
              ariaLabel="Склад-источник"
            />
          </WmsFieldLabel>
          <WmsFieldLabel label="Комментарий">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Например: размещение приёмки от 02.09"
            />
          </WmsFieldLabel>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={creating}
          >
            Отмена
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={creating || !warehouseId}
          >
            {creating ? "Создаём…" : "Создать черновик"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
